import { CloudUpload, HelpOutlineOutlined } from '@mui/icons-material';
import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Step,
    StepContent,
    StepLabel,
    Stepper,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { action, computed, observable, runInAction, makeObservable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import Modal from '@/misc/Modal';
import { CategoriaMinuta as ICategoriaMinuta, TipoControle, TipoMinuta } from '@/modelo';
import ImportacaoLoteService from '@/services/ImportacaoLoteService';
import ControleGabineteStore from '@/store/ControleGabineteStore';
import GabineteStore from '@/store/GabineteStore';
import ImportacaoLoteStore from '@/store/ImportacaoLoteStore';
import ParametroStore from '@/store/ParametroStore';
import ControleGabineteService from '@/services/ControleGabineteService';
import SelectServidores from '@/components/SelectServidores';
import ListaModeloMinutas from '@/components/ListaModeloMinutas';
import CategoriasMinuta from '@/components/CategoriasMinuta';
import './css/importacaoLoteCSV.css';

class ImportacaoLoteCSV extends React.Component {
    carregandoDados = false;

    constructor(props) {
        super(props);
        makeObservable(this, {
            carregandoDados: observable,
            tiposMinuta: computed,
            controlesGabMenuItem: computed,
            stepLabels: computed,
            handleFileUpload: action,
            handleChangeServidor: action,
            handleChangeControle: action,
            handleToggleCriarMinuta: action,
            handleRadioTipoMinuta: action,
            handleTogglePublicavel: action,
            handleToggleIncluirAposPauta: action,
            atualizaModelo: action,
            atualizaCategoria: action,
            handleChangeDescricao: action,
            executarImportacao: action,
        });
    }

    get tiposMinuta() {
        return ParametroStore.tiposMinuta.map(tipo => ({
            id: tipo.codigo,
            tipo: tipo.codigo === TipoMinuta.VOTO_FASE_FUTURA ? 'Cópia do voto com RR' : tipo.descricao,
        }));
    }

    get controlesGabMenuItem() {
        return ControleGabineteStore.listaDeControles
            .filter(card => card.descricao !== 'Importar do PJe')
            .filter(card => card.descricao !== 'Importar processos')
            .filter(card => card.descricao !== 'Visto à pauta')
            .filter(card => card.descricao !== 'Aguardando julgamento')
            .filter(card => card.descricao !== 'Aguardando Julgamento')
            .filter(card => card.descricao !== 'Para assinatura')
            .filter(card => card.configControle.regrasPossuiMinuta.length === 0)
            .map((card, index) => (
                <MenuItem value={card.numero} key={index}>
                    {card.descricao}
                </MenuItem>
            ));
    }

    get stepLabels(): string[] {
        const labels: string[] = ['Upload do CSV'];

        if (ImportacaoLoteStore.stepDistribuicaoVisivel) {
            labels.push('Servidor e Tarefa');
        }

        labels.push('Criar Minuta?');

        if (ImportacaoLoteStore.criarMinuta) {
            labels.push('Tipo de Minuta', 'Modelo', 'Categorias', 'Descrição do Signatário');
        }

        return labels;
    }

    abrirModal = async () => {
        ImportacaoLoteStore.abrirModal();
        runInAction(() => { this.carregandoDados = true; });

        try {
            await Promise.all([
                ControleGabineteStore.controlesCarregados
                    ? Promise.resolve()
                    : ControleGabineteService.atualizarControlesGabinete(),
                ParametroStore.parametrosCarregados
                    ? Promise.resolve()
                    : ParametroStore.inicializar(),
                GabineteStore.listaUsuariosGabinete.length > 0
                    ? Promise.resolve()
                    : GabineteStore.atualizarUsuariosGabinete(),
            ]);

            runInAction(() => {
                GabineteStore.listaUsuariosOrdenadoGabinete = GabineteStore.recuperarMinistrosServidoresOrdenados();
                const cardDistribuir = ControleGabineteStore.buscarPorTipo(TipoControle.DISTRIBUIR);
                if (cardDistribuir) {
                    ImportacaoLoteStore.controleGabAtribuido = cardDistribuir.numero;
                }
            });
        } finally {
            runInAction(() => { this.carregandoDados = false; });
        }
    };

    handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const arquivo = event.target.files?.[0];
        if (!arquivo) return;

        try {
            const resultado = await ImportacaoLoteService.parseCSV(arquivo);
            runInAction(() => {
                ImportacaoLoteStore.setProcessosCSV(
                    resultado.processos,
                    arquivo.name,
                    resultado.comColunasExtras
                );
            });
        } catch {
            runInAction(() => {
                ImportacaoLoteStore.setProcessosCSV([], '', false);
            });
        }
        // Limpa o input para permitir re-upload do mesmo arquivo
        event.target.value = '';
    };

    handleChangeServidor = (event) => {
        runInAction(() => {
            ImportacaoLoteStore.servidorAtribuido = event.target.value;
        });
    };

    handleChangeControle = (event) => {
        runInAction(() => {
            ImportacaoLoteStore.controleGabAtribuido = event.target.value;
        });
    };

    handleToggleCriarMinuta = () => {
        runInAction(() => {
            ImportacaoLoteStore.criarMinuta = !ImportacaoLoteStore.criarMinuta;
        });
    };

    handleRadioTipoMinuta = (event) => {
        const id = parseInt(event.target.value);
        runInAction(() => {
            ImportacaoLoteStore.tipoMinuta = id;
            ImportacaoLoteStore.publicavel = id === 2 || id === 3;
            ImportacaoLoteStore.modeloSelecionado = { codigo: -1, nome: '', conteudo: '', incluirAposPauta: false };
            ImportacaoLoteStore.incluirAposPauta = false;
        });
    };

    handleTogglePublicavel = () => {
        runInAction(() => {
            ImportacaoLoteStore.publicavel = !ImportacaoLoteStore.publicavel;
        });
    };

    handleToggleIncluirAposPauta = () => {
        runInAction(() => {
            ImportacaoLoteStore.incluirAposPauta = !ImportacaoLoteStore.incluirAposPauta;
        });
    };

    atualizaModelo = (conteudo, nome, _categoria, codigo, incluirAposPauta = false) => {
        runInAction(() => {
            ImportacaoLoteStore.modeloSelecionado = {
                codigo,
                nome,
                conteudo,
                incluirAposPauta: !!incluirAposPauta,
            };
            ImportacaoLoteStore.incluirAposPauta = ImportacaoLoteStore.incluirAposPauta || !!incluirAposPauta;
        });
    };

    atualizaCategoria = (categoriasSelecionadas: ICategoriaMinuta[]) => {
        runInAction(() => {
            ImportacaoLoteStore.categoriasSelecionadas = categoriasSelecionadas || [];
        });
    };

    handleChangeDescricao = (event) => {
        runInAction(() => {
            ImportacaoLoteStore.descricaoSignatario = event.target.value;
        });
    };

    executarImportacao = async () => {
        // Captura os valores antes de fechar o modal (que chama resetar())
        const config = {
            processosCSV: [...ImportacaoLoteStore.processosCSV],
            csvComColunasExtras: ImportacaoLoteStore.csvComColunasExtras,
            servidorAtribuido: ImportacaoLoteStore.servidorAtribuido,
            controleGabAtribuido: ImportacaoLoteStore.controleGabAtribuido,
            criarMinuta: ImportacaoLoteStore.criarMinuta,
            tipoMinuta: ImportacaoLoteStore.tipoMinuta,
            modeloSelecionado: { ...ImportacaoLoteStore.modeloSelecionado },
            categoriasSelecionadas: [...ImportacaoLoteStore.categoriasSelecionadas],
            descricaoSignatario: ImportacaoLoteStore.descricaoSignatario,
            publicavel: ImportacaoLoteStore.publicavel,
            incluirAposPauta: ImportacaoLoteStore.incluirAposPauta,
        };

        ImportacaoLoteStore.fecharModal();
        ImportacaoLoteStore.abrirProgresso();

        await ImportacaoLoteService.executarImportacaoLote(config);
    };

    isUltimoStep = (): boolean => {
        const { stepSemantico, criarMinuta } = ImportacaoLoteStore;
        if (!criarMinuta) return stepSemantico === 'criarMinuta';
        return stepSemantico === 'signatario';
    };

    renderStepContent = () => {
        const { stepSemantico } = ImportacaoLoteStore;

        switch (stepSemantico) {
            case 'csv':
                return this.renderStepCSV();
            case 'distribuicao':
                return this.renderStepDistribuicao();
            case 'criarMinuta':
                return this.renderStepCriarMinuta();
            case 'tipoMinuta':
                return this.renderStepTipoMinuta();
            case 'modelo':
                return this.renderStepModelo();
            case 'categorias':
                return this.renderStepCategorias();
            case 'signatario':
                return this.renderStepSignatario();
            default:
                return null;
        }
    };

    renderStepCSV = () => {
        const store = ImportacaoLoteStore;
        return (
            <div className="importacao-lote-csv-upload">
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 4 }}>
                    O arquivo deve conter um número de processo por linha. Opcionalmente, pode incluir
                    CPF do servidor e nome da tarefa separados por <b>;</b> ou <b>,</b>
                </Typography>
                <Typography variant="caption" color="textSecondary" component="div" style={{ fontFamily: 'monospace', background: '#f5f5f5', padding: '8px 12px', borderRadius: 4 }}>
                    <b>Formato simples:</b> 0001234-56.2024.5.00.0000<br />
                    <b>Formato completo:</b> 0001234-56.2024.5.00.0000;12345678901;Nome da Tarefa
                </Typography>
                <label htmlFor="csv-upload-input">
                    <Button
                        variant="outlined"
                        component="span"
                        startIcon={<CloudUpload />}
                    >
                        Selecionar arquivo CSV
                    </Button>
                </label>
                <input
                    id="csv-upload-input"
                    type="file"
                    accept=".csv,.txt"
                    onChange={this.handleFileUpload}
                />
                {store.nomeArquivo && (
                    <Typography variant="body2" color="textSecondary">
                        Arquivo: <b>{store.nomeArquivo}</b>
                    </Typography>
                )}
                {store.numerosProcessos.length > 0 && (
                    <>
                        <Typography variant="body2">
                            <b>{store.numerosProcessos.length}</b> número(s) de processo encontrado(s)
                            {store.csvComColunasExtras && (
                                <span style={{ color: '#1976d2', marginLeft: 8 }}>
                                    (com servidor/tarefa por processo)
                                </span>
                            )}
                        </Typography>
                        <div className="importacao-lote-csv-preview">
                            <ul>
                                {store.processosCSV.map((proc, i) => (
                                    <li key={i}>
                                        {proc.numeroProcesso}
                                        {store.csvComColunasExtras && (
                                            <span style={{ color: '#666', marginLeft: 12 }}>
                                                → {proc.cpfServidor || '(sem servidor)'}
                                                {' | '}
                                                {proc.nomeTarefa || '(sem tarefa)'}
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </>
                )}
            </div>
        );
    };

    renderStepDistribuicao = () => (
        <div className="importacao-lote-distribuicao">
            <SelectServidores
                handleChangeServidorDropDown={this.handleChangeServidor}
                servidorAtribuido={ImportacaoLoteStore.servidorAtribuido}
                movimentacaoIncompleta={true}
                lote={true}
                naoMostrarOpcaoManterResponsavel={true}
            />
            <FormControl fullWidth>
                <InputLabel id="label-tarefa-lote">Tarefa</InputLabel>
                <Select
                    label="Tarefa"
                    labelId="label-tarefa-lote"
                    value={ImportacaoLoteStore.controleGabAtribuido}
                    onChange={this.handleChangeControle}
                >
                    {this.controlesGabMenuItem}
                </Select>
            </FormControl>
        </div>
    );

    renderStepCriarMinuta = () => (
        <div>
            <FormControlLabel
                control={
                    <Switch
                        color="primary"
                        checked={ImportacaoLoteStore.criarMinuta}
                        onChange={this.handleToggleCriarMinuta}
                    />
                }
                label="Desejo criar minutas para os processos importados"
            />
            {!ImportacaoLoteStore.criarMinuta && (
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                    Os processos serão importados e distribuídos sem criação de minuta.
                </Typography>
            )}
        </div>
    );

    renderStepTipoMinuta = () => {
        const exibeIncluirAposPauta = ImportacaoLoteStore.tipoMinuta === TipoMinuta.DESPACHO
            || ImportacaoLoteStore.tipoMinuta === TipoMinuta.DECISAO;

        return (
            <div style={{ width: '50%' }}>
                <RadioGroup
                    name="tipoMinutaLote"
                    onChange={this.handleRadioTipoMinuta}
                    value={ImportacaoLoteStore.tipoMinuta}
                >
                    {this.tiposMinuta.map(tipoMinuta => (
                        <FormControlLabel
                            key={`radio-tipo-${tipoMinuta.id}`}
                            value={tipoMinuta.id}
                            control={<Radio color="primary" />}
                            label={tipoMinuta.tipo}
                            labelPlacement="end"
                        />
                    ))}
                </RadioGroup>
                <div className="divFlexRow" style={{ gap: '16px', paddingTop: '12px' }}>
                    <FormControlLabel
                        control={
                            <Switch
                                color="primary"
                                checked={ImportacaoLoteStore.publicavel}
                                onClick={this.handleTogglePublicavel}
                                title="Enviar ao DEJT"
                            />
                        }
                        label="Publicável"
                    />
                    {exibeIncluirAposPauta && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={ImportacaoLoteStore.incluirAposPauta}
                                    onChange={this.handleToggleIncluirAposPauta}
                                />
                            }
                            label="Após, à Pauta."
                        />
                    )}
                </div>
            </div>
        );
    };

    renderStepModelo = () => (
        <div style={{ width: '100%' }}>
            <ListaModeloMinutas
                tipo={ImportacaoLoteStore.tipoMinuta}
                atualizaModelo={this.atualizaModelo}
                incluirAposPauta={ImportacaoLoteStore.incluirAposPauta}
            />
        </div>
    );

    renderStepCategorias = () => (
        <div style={{ width: '100%' }}>
            <CategoriasMinuta
                minutasEmLote
                callbackSelecinarCategorias={this.atualizaCategoria}
                categoriasSelecionadas={ImportacaoLoteStore.categoriasSelecionadas}
            />
        </div>
    );

    renderStepSignatario = () => (
        <div>
            <TextField
                label="Descrição do signatário"
                onChange={this.handleChangeDescricao}
                value={ImportacaoLoteStore.descricaoSignatario}
                fullWidth
            />
            <Tooltip title="A função do signatário. Se é relator, redator, presidente, etc. Se for deixado em branco será usada a descrição padrão.">
                <HelpOutlineOutlined style={{ fontSize: '1rem', marginLeft: 8, verticalAlign: 'middle' }} />
            </Tooltip>
        </div>
    );

    render() {
        const store = ImportacaoLoteStore;
        const isUltimo = this.isUltimoStep();
        const labelBotaoPrimario = isUltimo
            ? (store.criarMinuta ? 'Executar Importação + Minutas' : 'Executar Importação')
            : 'Próximo';

        return (
            <Modal
                aberto={store.abertoModal}
                fecharModal={store.fecharModal}
                titulo="Importação em Lote via CSV"
                funcaoAcaoSecundaria={store.fecharModal}
                labelAcaoSecundaria="Cancelar"
                funcaoAcaoPrimaria={isUltimo ? this.executarImportacao : store.avancarStep}
                labelAcaoPrimaria={labelBotaoPrimario}
                acaoPrimariaDisabled={!store.podeAvancar || this.carregandoDados}
                fullWidth
                maxWidth="md"
                PaperProps={{ style: { minHeight: '500px' } }}
                dialogContentStyle={{
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto',
                }}
                outrasAcoes={
                    store.stepIndex > 0 ? (
                        <Button variant="outlined" onClick={store.voltarStep}>
                            Voltar
                        </Button>
                    ) : undefined
                }
            >
                <Stepper activeStep={store.stepIndex} orientation="vertical">
                    {this.stepLabels.map((label, index) => (
                        <Step key={label} completed={store.stepIndex > index}>
                            <StepLabel>{label}</StepLabel>
                            {store.stepIndex === index && (
                                <StepContent>
                                    <div className="importacao-lote-step-content">
                                        {this.renderStepContent()}
                                    </div>
                                </StepContent>
                            )}
                        </Step>
                    ))}
                </Stepper>
            </Modal>
        );
    }
}

export default observer(ImportacaoLoteCSV);
